import type { AdapterFetchOptions, AdapterResult, MarketSourceAdapter } from '@/types/adapters';
import type { NormalizedMarketRow } from '@/types/market';
import { appConfig } from '@/config/app';
import { SOURCE_REGISTRY } from '@/data/sourceRegistry';
import { loadSnapshotManifest } from '@/data/snapshotManifest';
import { fetchCsv, parseCsv } from '@/utils/csv';
import { pctChange, toNum } from '@/utils/numbers';

const DEFAULT_FILE = 'trerc_county_monthly_sample.csv';
const EXPECTED_FIELDS = 15; // used for fieldCompleteness scoring

/**
 * TRERC (Texas Real Estate Research Center) adapter. This is the primary,
 * always-on county-level sale-flow source for Phase 1. Reads a same-origin
 * CSV snapshot -- see public/data-snapshots/ -- rather than calling TRERC
 * directly, since TRERC does not offer a browser-fetchable public API.
 */
export const trercAdapter: MarketSourceAdapter = {
  meta: SOURCE_REGISTRY.trerc,

  async fetch(options?: AdapterFetchOptions): Promise<AdapterResult> {
    const warnings: string[] = [];
    const manifest = await loadSnapshotManifest(options?.signal);
    const entry = manifest.trerc;
    const file = entry?.file ?? DEFAULT_FILE;
    const isSampleData = entry?.isSample ?? true;

    let text: string;
    try {
      text = await fetchCsv(`${appConfig.dataSnapshotBasePath}/${file}`, options?.signal);
    } catch (err) {
      warnings.push(`trerc: could not load ${file}: ${(err as Error).message}`);
      return { rows: [], warnings, isSampleData };
    }

    const records = parseCsv(text);
    const rows: NormalizedMarketRow[] = [];

    for (const r of records) {
      const geoId = r.county_fips?.trim();
      if (!geoId) {
        warnings.push('trerc: skipped a row with no county_fips');
        continue;
      }

      const medianSalePrice = toNum(r.median_sale_price);
      const medianSalePricePrevPeriod = toNum(r.median_sale_price_prev);
      const medianDaysOnMarket = toNum(r.dom_median);
      const medianDaysOnMarketPrevPeriod = toNum(r.dom_median_prev);
      const activeListings = toNum(r.active_listings);
      const activeListingsPrevPeriod = toNum(r.active_listings_prev);
      const monthsOfInventory = toNum(r.months_inventory);
      const monthsOfInventoryPrevPeriod = toNum(r.months_inventory_prev);
      const closedSales = toNum(r.closed_sales);
      const closedSalesPrevPeriod = toNum(r.closed_sales_prev);
      const newListings = toNum(r.new_listings);
      const pendingSales = toNum(r.pending_sales);
      const saleToListRatioPct = toNum(r.sale_to_list_ratio_pct);
      const priceReductionSharePct = toNum(r.price_reduction_share_pct);

      const filledCount = [
        medianSalePrice,
        medianDaysOnMarket,
        activeListings,
        monthsOfInventory,
        closedSales,
        newListings,
        pendingSales,
        saleToListRatioPct,
        priceReductionSharePct,
        medianSalePricePrevPeriod,
        medianDaysOnMarketPrevPeriod,
        activeListingsPrevPeriod,
        monthsOfInventoryPrevPeriod,
        closedSalesPrevPeriod,
      ].filter((v) => v !== undefined).length;

      rows.push({
        geoLevel: 'county',
        geoId,
        geoName: r.county_name?.trim() || geoId,
        sourceId: 'trerc',
        sourceLabel: entry?.label ?? SOURCE_REGISTRY.trerc.label,
        periodLabel: r.period?.trim() || entry?.periodLabel || 'unknown',
        publishedAt: r.published_at?.trim() || entry?.publishedAt || new Date().toISOString().slice(0, 10),
        retrievedAt: new Date().toISOString(),
        medianSalePrice,
        medianSalePricePrevPeriod,
        medianSalePriceYoyPct: pctChange(medianSalePrice, medianSalePricePrevPeriod),
        medianDaysOnMarket,
        medianDaysOnMarketPrevPeriod,
        activeListings,
        activeListingsPrevPeriod,
        monthsOfInventory,
        monthsOfInventoryPrevPeriod,
        closedSales,
        closedSalesPrevPeriod,
        newListings,
        pendingSales,
        saleToListRatioPct,
        priceReductionSharePct,
        fieldCompleteness: filledCount / EXPECTED_FIELDS,
      });
    }

    return { rows, warnings, isSampleData };
  },
};
